import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import { timeAgo } from "@/lib/api";
import { useArticlePath } from "@/lib/i18n";
import { CAT_COLORS, catLabel } from "../morestories/LiveFeed";
import { homeTrack } from "../shared";
import { ProgressBar } from "./ResumeTimeline";

const FALLBACK = "/thumbs/market_globe.jpg";

export const WorthResuming = ({ item, premiumUser }: { item: any; premiumUser: boolean }) => {
  const toArticle = useArticlePath();
  if (!item) return null;
  const started = item.reason === "You started this";
  const gated = item.access_level === "premium" && !premiumUser;
  const dest = gated ? `/premium?read=${item.slug}` : `${toArticle(item.slug)}${started ? "?resume=1" : ""}`;
  const cat = item.categories?.[0];
  const typeLabel = (item.read_minutes || 0) >= 7 ? "Deep dive" : catLabel(cat);
  return (
    <div className="rounded-xl border border-[#242A39] bg-[#080B14] p-3.5 min-w-0 w-full" data-testid="worth-resuming">
      <p className="font-heading font-bold text-[#F5F7FB] text-[14px]">Worth resuming</p>
      <Link to={dest} data-testid="worth-resuming-link"
        onClick={() => homeTrack("jump_back_in", `worth_${item.slug}`)}
        className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A955FF]/60 rounded-lg">
        <span className="block rounded-[8px] overflow-hidden border border-white/[0.05] mt-2.5">
          <img src={item.hero_image_url || FALLBACK} alt={item.title} loading="lazy"
            className="w-full h-[96px] object-cover motion-safe:transition-transform motion-safe:duration-300 group-hover:scale-[1.02]" />
        </span>
        <span className="line-clamp-2 font-news text-[14px] font-semibold text-[#F5F7FB] group-hover:text-white leading-snug mt-2.5 motion-safe:transition-colors">
          {item.title}
        </span>
        <span className="block font-mono text-[8.5px] uppercase tracking-[0.14em] text-[#5d6577] mt-1.5">
          {typeLabel}{item.read_minutes ? ` · ${item.read_minutes} min read` : ""}
        </span>
        {started && <ProgressBar pct={item.progress_percent} className="mt-2.5" />}
        <span className="flex items-center justify-between gap-2 mt-2.5 pt-2.5 border-t border-white/[0.05]">
          <span data-testid="worth-resuming-reason"
            className="font-mono text-[8.5px] font-bold uppercase tracking-[0.14em] text-[#C08BFF] truncate">
            {started ? `${item.progress_percent}% read` : item.reason}
          </span>
          <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#10D7F2] group-hover:text-white shrink-0 motion-safe:transition-colors">
            {gated ? "Resume with Premium" : started ? "Resume" : "Read"}
            <ArrowRight className="h-3 w-3 motion-safe:transition-transform motion-safe:duration-200 group-hover:translate-x-0.5" aria-hidden />
          </span>
        </span>
      </Link>
    </div>
  );
};

const SIGNALS: Record<string, { label: string; Icon: any; cls: string }> = {
  rising: { label: "Rising", Icon: TrendingUp, cls: "text-[#10D7F2]" },
  high_interest: { label: "High interest", Icon: TrendingUp, cls: "text-[#10D7F2]" },
  cooling: { label: "Cooling", Icon: TrendingDown, cls: "text-[#FB7185]" },
  for_you: { label: "For you", Icon: Sparkles, cls: "text-[#C08BFF]" },
};

export const TrendingForYou = ({ items }: { items: any[] }) => {
  const toArticlePathFn = useArticlePath();
  return (
    <div className="rounded-xl border border-[#242A39] bg-[#080B14] overflow-hidden min-w-0 w-full"
      data-testid="trending-for-you">
      <p className="px-3.5 pt-3 pb-1.5 font-heading font-bold text-[#F5F7FB] text-[14px]">Trending for you</p>
      {items.map((t) => {
        const sig = SIGNALS[t.signal] || SIGNALS.for_you;
        return (
          <Link key={t.slug} to={toArticlePathFn(t.slug)} data-testid={`tfy-row-${t.slug}`}
            onClick={() => homeTrack("jump_back_in", `tfy_${t.slug}`)}
            title={t.reason}
            className="group flex items-center gap-2.5 px-3.5 py-2.5 border-t border-white/[0.04] hover:bg-white/[0.03] motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A955FF]/60">
            {t.hero_image_url && (
              <img src={t.hero_image_url} alt="" loading="lazy" width={40} height={40}
                className="w-10 h-10 object-cover rounded-[6px] shrink-0 border border-white/[0.05]" />
            )}
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 font-news text-[12px] font-semibold text-[#e6e9f0] group-hover:text-white leading-snug motion-safe:transition-colors">
                {t.title}
              </span>
              <span className="block font-mono text-[8px] uppercase tracking-[0.14em] text-[#5d6577] mt-[3px]">
                {t.read_minutes ? `${t.read_minutes} min read` : timeAgo(t.published_at)}
              </span>
            </span>
            <span data-testid={`tfy-signal-${t.slug}`}
              className={`inline-flex items-center gap-1 shrink-0 font-mono text-[8px] font-bold uppercase tracking-[0.12em] ${sig.cls}`}>
              <sig.Icon className="h-3 w-3" aria-hidden /> {sig.label}
            </span>
          </Link>
        );
      })}
      {!items.length && (
        <p className="px-3.5 py-4 text-[11.5px] text-[#929AAC]" data-testid="tfy-empty">
          No trending signals yet.
        </p>
      )}
      <Link to="/latest" data-testid="tfy-see-more"
        onClick={() => homeTrack("jump_back_in", "see_more_insights")}
        className="group flex items-center gap-1 px-3.5 py-2.5 border-t border-white/[0.05] text-[11px] font-bold text-[#10D7F2] hover:text-white motion-safe:transition-colors">
        See more insights
        <ArrowRight className="h-3 w-3 motion-safe:transition-transform motion-safe:duration-200 group-hover:translate-x-0.5" aria-hidden />
      </Link>
    </div>
  );
};
